/**
 * Canonical Agents (escalation agent picker)
 *
 * The agents a user can put in a room, for "Continue in Chat"
 * (EscalateToChatButton.tsx). Same source and rules as the chat plugin's own
 * picker (plugins/chat/ui/pages/ChatPage.tsx `loadCanonicalAgents`,
 * plugins/chat/ui/lib/canonical-agent.ts):
 *   - GET /api/plugins/agent-manager/custom-agents/available — built-ins plus
 *     the caller's own, shared and workspace custom agents; an older
 *     agent-manager without custom agents only has /agent/definitions.
 *   - every stored version comes back, so keep the newest per agent;
 *   - agents a plugin retired (`presentation.showInLauncher: false`) aren't
 *     offered for new rooms.
 * Kept as a small local copy since those helpers live in the chat plugin's
 * own repo, not a package the core frontend can import.
 */
import { buildAssistantApiClient } from './assistant-api-client';

export interface CanonicalAgentEntry {
  id: string;
  version?: string;
  presentation?: { showInLauncher?: boolean };
  sourceRef?: { aliases?: string[] };
  identity?: { name?: string; displayName?: string; description?: string };
  role?: { roleId?: string; legacyRole?: string };
}

export function agentDisplayName(agent: CanonicalAgentEntry): string {
  return (
    agent.identity?.displayName ??
    agent.identity?.name ??
    agent.role?.roleId ??
    agent.role?.legacyRole ??
    agent.sourceRef?.aliases?.[0] ??
    agent.id
  );
}

/** `1.10.2` > `1.9.0`; anything non-numeric sorts as 0, and equal numbers fall back to plain string order. */
function compareVersions(a: string | undefined, b: string | undefined): number {
  const parts = (v: string | undefined) => (v ?? '').split(/[.+-]/).map((p) => (/^\d+$/.test(p) ? Number(p) : 0));
  const left = parts(a);
  const right = parts(b);
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return (a ?? '').localeCompare(b ?? '');
}

/** Each offered agent once, at its newest version, sorted by name. */
export function pickableAgents(entries: readonly CanonicalAgentEntry[]): CanonicalAgentEntry[] {
  const newest = new Map<string, CanonicalAgentEntry>();
  for (const entry of entries) {
    if (!entry || typeof entry.id !== 'string') continue;
    const current = newest.get(entry.id);
    if (!current || compareVersions(entry.version, current.version) > 0) newest.set(entry.id, entry);
  }
  return [...newest.values()]
    .filter((entry) => entry.presentation?.showInLauncher !== false)
    .sort((a, b) => agentDisplayName(a).localeCompare(agentDisplayName(b)));
}

export async function listCanonicalAgents(): Promise<CanonicalAgentEntry[]> {
  const api = buildAssistantApiClient();
  try {
    let data: unknown;
    try {
      data = await api.get<CanonicalAgentEntry[]>('/api/plugins/agent-manager/custom-agents/available');
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status !== 404) throw err;
      data = await api.get<CanonicalAgentEntry[]>('/api/plugins/agent-manager/agent/definitions');
    }
    return Array.isArray(data) ? pickableAgents(data as CanonicalAgentEntry[]) : [];
  } catch (err) {
    console.warn('[AssistantOverlay] Failed to load canonical agents:', err);
    return [];
  }
}
